<!--
  ProductEditorNotFound — empty state for the canonical Product Editor
  when GET /api/campaign_products/:id returns 404.

  Bug #81 root cause: when a SuperAdmin impersonates a distributor that
  doesn't own the campaign_product in the URL (tenant-mismatched deep
  link), the page-mount load 404s. The shell previously rendered a
  generic "Failed to load product. Retry" alert that just looped — the
  retry couldn't fix the tenancy mismatch, and the SA had no signal
  about what was wrong or how to recover.

  This component renders an actionable empty state. Two flavors:

    - Impersonating: headline names the impersonated distributor, copy
      tells the SA they're in an impersonation session, two actions:
      "Exit impersonation" (calls impersonationStore.stopImpersonation
      and hard-navigates back to /admin, mirroring the in-banner exit
      affordance) and "Back to stores" (router.push to the distributor
      stores list).

    - Not impersonating: generic "This product no longer exists" with
      only "Back to stores" (a regular Owner navigated to a deleted
      product).

  Why "Back to stores" (and not the owning store editor): the 404 means
  we never received a serverState payload, so we don't know which store
  this product belonged to. The stores list is the closest safe parent
  surface that always exists for an Owner / Builder / impersonating SA.
-->
<template>
    <div
        data-testid="product-editor-not-found"
        class="product-editor-not-found"
        role="status"
    >
        <div class="product-editor-not-found-card">
            <span
                class="material-icons product-editor-not-found-icon"
                aria-hidden="true"
            >search_off</span>

            <h2
                data-testid="product-editor-not-found-headline"
                class="product-editor-not-found-headline"
            >
                <template v-if="isImpersonating">
                    This product isn't available for {{ distributorName }}
                </template>
                <template v-else>
                    This product no longer exists
                </template>
            </h2>

            <p
                data-testid="product-editor-not-found-sub"
                class="product-editor-not-found-sub"
            >
                <template v-if="isImpersonating">
                    You're currently impersonating {{ distributorName }}. Exit
                    impersonation to return to your own session, or navigate
                    back to the stores list.
                </template>
                <template v-else>
                    The product you're looking for may have been deleted, or
                    the link is no longer valid.
                </template>
            </p>

            <div class="product-editor-not-found-actions">
                <button
                    v-if="isImpersonating"
                    type="button"
                    data-testid="product-editor-not-found-exit"
                    class="product-editor-not-found-btn product-editor-not-found-btn-primary"
                    @click="onExitImpersonation"
                >
                    Exit impersonation
                </button>
                <button
                    type="button"
                    data-testid="product-editor-not-found-back"
                    class="product-editor-not-found-btn"
                    :class="{ 'product-editor-not-found-btn-primary': !isImpersonating }"
                    @click="onBack"
                >
                    Back to stores
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { describeError } from "@/utils/safeErrorLog";
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useImpersonationStore } from "@/stores/impersonation";

const router = useRouter();
const impersonation = useImpersonationStore();

const isImpersonating = computed(() => Boolean(impersonation.isActive));

const distributorName = computed(
    () => impersonation.distributorName || "this distributor",
);

async function onExitImpersonation() {
    try {
        await impersonation.stopImpersonation();
        // Mirror ImpersonationBanner.onExit: a hard navigation back to
        // /admin forces the SPA to re-fetch the restored SuperAdmin
        // capabilities. A soft router.push would be bounced by the
        // /admin requireCapability("superadmin.access") guard because
        // the in-memory capability set still reflects the distributor
        // session until the page reloads.
        if (typeof window !== "undefined" && window.location) {
            window.location.assign("/admin");
        }
    } catch (err) {
        // Match ImpersonationBanner: surface but don't crash. The
        // store leaves state intact on failure so the empty state
        // remains visible and the SA can retry.
        // eslint-disable-next-line no-console
        console.error("Stop impersonation failed:", describeError(err));
    }
}

function onBack() {
    // The 404 means we never received the campaign_product payload,
    // so we don't know the owning store_id. The distributor stores
    // list is the closest safe parent surface.
    router.push({ name: "distributor_stores" });
}
</script>

<style scoped>
.product-editor-not-found {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1 1 auto;
    min-height: 0;
    padding: 48px 24px;
}

.product-editor-not-found-card {
    max-width: 480px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 32px;
    background: var(--tf-surface, #ffffff);
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 8px;
}

.product-editor-not-found-icon {
    font-size: 48px;
    color: var(--tf-text-subdued, #6b7280);
    line-height: 1;
}

.product-editor-not-found-headline {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--tf-text, #111827);
    line-height: 1.3;
}

.product-editor-not-found-sub {
    margin: 0;
    font-size: 14px;
    color: var(--tf-text-subdued, #6b7280);
    line-height: 1.5;
}

.product-editor-not-found-actions {
    display: flex;
    gap: 12px;
    margin-top: 8px;
    flex-wrap: wrap;
    justify-content: center;
}

.product-editor-not-found-btn {
    padding: 8px 16px;
    border-radius: 4px;
    border: 1px solid var(--tf-border, #d0d5dd);
    background: var(--tf-surface, #ffffff);
    color: var(--tf-text, #111827);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
}

.product-editor-not-found-btn:hover {
    background: var(--tf-surface-hover, rgba(0, 0, 0, 0.04));
}

.product-editor-not-found-btn-primary {
    background: var(--tf-primary, #1d4ed8);
    border-color: var(--tf-primary, #1d4ed8);
    color: white;
}

.product-editor-not-found-btn-primary:hover {
    background: var(--tf-primary-hover, #1e40af);
}
</style>
